/**
 * MessagePreview — мобильный preview сообщения как в Telegram.
 *
 * Цель: админ видит ровно то, что увидит клиент на телефоне — bubble с
 * правильным форматированием, переносами, медиа. Решает проблему «editor
 * растянут на весь экран — текст пишется одной строкой, а на телефоне
 * это получается каша».
 *
 * Ширина 340 px ≈ iPhone safe-area. Использует HTML-санитайзер,
 * идентичный backend `_tiptap_html_to_tg` — чтобы preview совпадал с
 * реальным TG-рендерингом.
 */
import { useMemo } from "react";
import { Typography, Avatar } from "antd";

const TG_ALLOWED = new Set([
  "b", "strong", "i", "em", "u", "ins", "s", "strike", "del",
  "span", "tg-spoiler", "code", "pre", "a", "blockquote",
]);

/** Frontend-копия `_tiptap_html_to_tg` из api/routes/chat.py — должны давать
 *  одинаковый output чтобы preview соответствовал реально отправленному. */
function sanitizeForTg(html: string): string {
  if (!html) return "";
  let s = html;
  s = s.replace(/<br\s*\/?>/gi, "\n");
  s = s.replace(/<p[^>]*>/gi, "");
  s = s.replace(/<\/p>/gi, "\n\n");
  s = s.replace(/<(\/?)([a-zA-Z][a-zA-Z0-9-]*)\b[^>]*>/g,
    (m, _slash, tag) => TG_ALLOWED.has(tag.toLowerCase()) ? m : "");
  return s.replace(/\n{3,}/g, "\n\n").trim();
}

/** Преобразует TG-safe HTML с \n в HTML с <br> — для отображения в браузере. */
function tgTextToHtml(text: string): string {
  return text.replace(/\n/g, "<br>");
}

export interface AttachmentPreview {
  uid: string;
  file: File;
  kind: "photo" | "video" | "audio" | "document" | "voice";
}

interface Props {
  html: string;
  attachments?: AttachmentPreview[];
  collapsed?: boolean;
}

const KIND_ICON: Record<AttachmentPreview["kind"], string> = {
  photo: "🖼", video: "▶️", audio: "🎵", voice: "🎙", document: "📄",
};

const formatSize = (b: number) =>
  b < 1024 ? `${b} B` :
  b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` :
  `${(b / 1024 / 1024).toFixed(1)} MB`;

export const MessagePreview = ({ html, attachments = [] }: Props) => {
  const { tgHtml, isEmpty } = useMemo(() => {
    const tg = sanitizeForTg(html);
    return { tgHtml: tgTextToHtml(tg), isEmpty: !tg };
  }, [html]);

  // Сейчас (для timestamp)
  const now = useMemo(() => {
    const d = new Date();
    return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  }, [tgHtml, attachments.length]);

  return (
    <div style={{
      width: 340, minWidth: 340, maxWidth: 340,
      height: "100%",                    // заполняем всю высоту parent flex-item
      display: "flex", flexDirection: "column",
      background: "#0e1621",
      borderRadius: 12, overflow: "hidden",
      boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, ' +
        '"Helvetica Neue", Arial, sans-serif',
      color: "#e3e8eb",
    }}>
      {/* iPhone-like status bar — fixed height */}
      <div style={{
        flex: "0 0 auto", height: 30, background: "#000",
        display: "flex", justifyContent: "space-between",
        alignItems: "center", padding: "0 18px",
        fontSize: 13, fontWeight: 600, color: "#fff",
      }}>
        <span>9:41</span>
        <span style={{ fontSize: 12, opacity: 0.85 }}>📶 🛜 🔋</span>
      </div>

      {/* TG chat header — fixed height */}
      <div style={{
        flex: "0 0 auto",
        background: "#17212b", padding: "10px 14px",
        display: "flex", alignItems: "center", gap: 10,
        borderBottom: "1px solid #0a0e13",
      }}>
        <Avatar size={36} style={{ background: "#5288c1" }}>B</Avatar>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>
            Bemli
          </div>
          <div style={{ fontSize: 11, color: "#7d8e9a" }}>в сети</div>
        </div>
      </div>

      {/* CHAT AREA — single scroll container.
          KEY: minHeight:0 — без него flex:1 child не схлопывается ниже
          intrinsic content size и overflow не активируется (типичный
          CSS-gotcha с nested flex parents). */}
      <div style={{
        flex: "1 1 0", minHeight: 0,
        overflowY: "auto", overflowX: "hidden",
        background: "linear-gradient(135deg, #0e1621 0%, #182533 100%)",
      }}>
        {/* Inner padding-wrapper. minHeight:100% + justify-end → контент
            прижат к низу когда его мало, и нормально скроллит когда много. */}
        <div style={{
          minHeight: "100%", padding: 14, boxSizing: "border-box",
          display: "flex", flexDirection: "column",
          justifyContent: "flex-end", gap: 6,
        }}>
          {isEmpty && attachments.length === 0 ? (
            <div style={{ textAlign: "center", color: "#5f6e7a", fontSize: 12,
                          margin: "auto 0" }}>
              ✏️ Здесь появится превью того,
              <br />что увидит клиент на телефоне
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6,
                          alignItems: "flex-end" }}>
              {attachments.map((a) => (
                <AttachmentBubble key={a.uid} att={a} />
              ))}
              {!isEmpty && (
                <div className="tg-message-bubble" style={{
                  background: "#2b5278", color: "#fff",
                  padding: "8px 12px 18px",
                  borderRadius: "14px 14px 4px 14px",
                  maxWidth: "85%", fontSize: 14, lineHeight: 1.4,
                  position: "relative", wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                }}>
                  <div dangerouslySetInnerHTML={{ __html: tgHtml }} />
                  <span style={{
                    position: "absolute", right: 8, bottom: 4,
                    fontSize: 10, color: "rgba(255,255,255,0.7)",
                    display: "flex", alignItems: "center", gap: 2,
                  }}>
                    {now} <span style={{ fontSize: 11 }}>✓✓</span>
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Phone footer (decorative) — fixed */}
      <div style={{ flex: "0 0 auto",
                    background: "#17212b", padding: "6px 14px",
                    borderTop: "1px solid #0a0e13", textAlign: "center" }}>
        <Typography.Text style={{ color: "#5f6e7a", fontSize: 10 }}>
          📱 Так клиент увидит сообщение
        </Typography.Text>
      </div>

      {/* CSS-in-JS для контента бабла */}
      <style>{`
        .tg-message-bubble code {
          background: rgba(0,0,0,0.25); padding: 1px 4px;
          border-radius: 3px; font-size: 13px;
          font-family: ui-monospace, "SF Mono", Menlo, monospace;
        }
        .tg-message-bubble pre {
          background: rgba(0,0,0,0.25); padding: 6px 10px;
          border-radius: 6px; font-size: 12px;
          font-family: ui-monospace, "SF Mono", Menlo, monospace;
          white-space: pre-wrap; margin: 4px 0;
        }
        .tg-message-bubble a {
          color: #8cc2ff; text-decoration: underline;
        }
        .tg-message-bubble blockquote {
          border-left: 3px solid #5288c1; padding-left: 8px;
          margin: 6px 0; color: rgba(255,255,255,0.85);
        }
      `}</style>
    </div>
  );
};

// ─── Attachment-bubble — мини-карточка в TG-стиле ───────────────────────────

const AttachmentBubble = ({ att }: { att: AttachmentPreview }) => {
  const isImage = att.kind === "photo" && att.file.type.startsWith("image/");
  const [previewUrl] = useImagePreviewUrl(isImage ? att.file : null);

  if (isImage && previewUrl) {
    return (
      <div style={{
        maxWidth: "85%", borderRadius: 12, overflow: "hidden",
        background: "#2b5278",
      }}>
        <img src={previewUrl} alt={att.file.name}
             style={{ display: "block", maxWidth: 280, maxHeight: 280,
                      width: "auto", height: "auto" }} />
        <div style={{ padding: "4px 10px", fontSize: 10, opacity: 0.7,
                      color: "#fff", textAlign: "right" }}>
          {formatSize(att.file.size)} · 9:41 ✓✓
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: "#2b5278", color: "#fff",
      padding: "8px 12px", borderRadius: 12, maxWidth: "85%",
      display: "flex", gap: 10, alignItems: "center", minWidth: 160,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 18,
        background: "rgba(255,255,255,0.15)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 18, flexShrink: 0,
      }}>
        {KIND_ICON[att.kind]}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {att.file.name}
        </div>
        <div style={{ fontSize: 11, opacity: 0.75, marginTop: 1 }}>
          {formatSize(att.file.size)} · {att.kind}
        </div>
      </div>
    </div>
  );
};

// ─── helpers ────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";

function useImagePreviewUrl(file: File | null): [string | null] {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) { setUrl(null); return; }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return [url];
}
